import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";

export interface DaycareHotelSyncSettings {
  isEnabled: boolean;
  sourceClinicId: string | null;
}

const EMPTY: DaycareHotelSyncSettings = { isEnabled: false, sourceClinicId: null };

export const daycareHotelSyncSettingsKey = (customerId: string | null) => [
  "daycare-hotel-sync-settings",
  customerId,
];

export function useDaycareHotelSyncSettings() {
  const { data: customerId = null } = useCurrentClinicCustomerId();

  return useQuery({
    queryKey: daycareHotelSyncSettingsKey(customerId),
    queryFn: async (): Promise<DaycareHotelSyncSettings> => {
      if (!customerId) return EMPTY;
      const { data, error } = await supabase
        .from("daycare_hotel_sync_settings" as never)
        .select("is_enabled, source_clinic_id")
        .eq("customer_id", customerId)
        .maybeSingle() as unknown as {
          data: { is_enabled: boolean; source_clinic_id: string | null } | null;
          error: unknown;
        };
      if (error) throw error;
      if (!data) return EMPTY;
      return { isEnabled: data.is_enabled, sourceClinicId: data.source_clinic_id };
    },
    enabled: !!customerId,
  });
}

export function useEnableDaycareHotelSync() {
  const queryClient = useQueryClient();
  const { data: customerId = null } = useCurrentClinicCustomerId();

  return useMutation({
    mutationFn: async ({ sourceClinicId }: { sourceClinicId: string }) => {
      if (!customerId) throw new Error("no_customer");
      const { error } = await supabase.rpc("enable_daycare_hotel_settings_sync" as never, {
        p_customer_id: customerId,
        p_source_clinic_id: sourceClinicId,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: daycareHotelSyncSettingsKey(customerId) });
      queryClient.invalidateQueries({ queryKey: ["stay-schedule-defaults"] });
      queryClient.invalidateQueries({ queryKey: ["stay-daily-rates"] });
      queryClient.invalidateQueries({ queryKey: ["overnight-config"] });
      queryClient.invalidateQueries({ queryKey: ["stay-plans"] });
    },
  });
}

export function useDisableDaycareHotelSync() {
  const queryClient = useQueryClient();
  const { data: customerId = null } = useCurrentClinicCustomerId();

  return useMutation({
    mutationFn: async () => {
      if (!customerId) throw new Error("no_customer");
      const { error } = await supabase.rpc("disable_daycare_hotel_settings_sync" as never, {
        p_customer_id: customerId,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: daycareHotelSyncSettingsKey(customerId) });
      queryClient.invalidateQueries({ queryKey: ["stay-schedule-defaults"] });
      queryClient.invalidateQueries({ queryKey: ["stay-daily-rates"] });
      queryClient.invalidateQueries({ queryKey: ["overnight-config"] });
      queryClient.invalidateQueries({ queryKey: ["stay-plans"] });
    },
  });
}
